import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import {
    AlertCircle, ArrowLeft, Check, Loader2, Plus, Save, Search, Trash2,
} from 'lucide-react';
import { useDownloadAttachment, useLoadHttpClientEmpty } from '../../../App.hooks';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type ClassificationData = {
    ClassificationTypeId: string;
    ClassificationTypeName: string;
    CodeParameterName: string | null;
    DescriptionParameterName: string | null;
    MapToOotbIfcParams: boolean;
    MapToIfcClassification: boolean;
    UpdateCategoryBindings: boolean;
};

type ClassificationMap = Record<string, ClassificationData>;

const makeEntry = (id: string, name: string): ClassificationData => ({
    ClassificationTypeId: id,
    ClassificationTypeName: name,
    CodeParameterName: null,
    DescriptionParameterName: null,
    MapToOotbIfcParams: false,
    MapToIfcClassification: false,
    UpdateCategoryBindings: false,
});

const DYNAMIC_DATA_TYPE = 'Standards';
const DYNAMIC_DATA_NAME = 'Classification';

/* ------------------------------------------------------------------ */
/*  GraphQL                                                            */
/* ------------------------------------------------------------------ */

const GET_PROJECT_CLASSIFICATION_DATA = gql`
    query getProjectClassificationData($id: String) {
        Project(id: $id) {
            dynamicData { id name type data setData }
        }
    }
`;

const SET_PROJECT_DYNAMIC_DATA = gql`
    mutation setClassificationDynamicData($projectId: String, $data: DynamicDataInput) {
        Project(id: $projectId) { setDynamicData(data: $data) }
    }
`;

const GET_CLASSIFICATION_TYPES = gql`
    query getClassificationTypesForStandard($search: String) {
        ClassificationTypeList(
            where: { name: { contains: $search } }
            order: { name: ASC }
            first: 20
        ) {
            nodes { id name }
        }
    }
`;

/* ------------------------------------------------------------------ */
/*  Toggle switch                                                      */
/* ------------------------------------------------------------------ */

type ToggleProps = { checked: boolean; onChange: (v: boolean) => void; label: string; description?: string };

const Toggle = ({ checked, onChange, label, description }: ToggleProps) => (
    <label
        className="flex items-start gap-3 cursor-pointer select-none"
        style={{ padding: '10px 0' }}
    >
        {/* Track */}
        <span
            className="flex-shrink-0 relative"
            style={{ width: 36, height: 20, marginTop: 1 }}
        >
            <input
                type="checkbox"
                checked={checked}
                onChange={e => onChange(e.target.checked)}
                style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
            />
            <span
                style={{
                    display: 'block',
                    width: 36,
                    height: 20,
                    borderRadius: 10,
                    backgroundColor: checked ? 'var(--accent)' : 'var(--border-default)',
                    transition: 'background-color 0.15s',
                }}
            />
            <span
                style={{
                    position: 'absolute',
                    top: 2,
                    left: checked ? 18 : 2,
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: '#fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,.2)',
                    transition: 'left 0.15s',
                }}
            />
        </span>
        <span>
            <span className="text-sm font-medium" style={{ color: 'var(--text-primary)', display: 'block' }}>
                {label}
            </span>
            {description && (
                <span className="text-xs" style={{ color: 'var(--text-tertiary)', display: 'block', marginTop: 1 }}>
                    {description}
                </span>
            )}
        </span>
    </label>
);

/* ------------------------------------------------------------------ */
/*  Classification type combobox                                       */
/* ------------------------------------------------------------------ */

type ClassificationTypePickerProps = {
    selectedId: string;
    selectedName: string;
    excludeIds?: string[];
    placeholder?: string;
    onChange: (id: string, name: string) => void;
};

const ClassificationTypePicker = ({ selectedId, selectedName, excludeIds = [], placeholder = 'Select classification typeâ€¦', onChange }: ClassificationTypePickerProps) => {
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 250);
        return () => clearTimeout(t);
    }, [search]);

    const { data, loading } = useQuery(GET_CLASSIFICATION_TYPES, {
        variables: { search: debouncedSearch },
        fetchPolicy: 'cache-first',
    });

    const results = useMemo(() => {
        const nodes = (data?.ClassificationTypeList?.nodes ?? []) as Array<{ id: string; name: string }>;
        return nodes.filter(n => !excludeIds.includes(n.id));
    }, [data, excludeIds]);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const handleSelect = (id: string, name: string) => {
        onChange(id, name);
        setSearch('');
        setOpen(false);
    };

    return (
        <div ref={containerRef} style={{ position: 'relative' }}>
            <div
                className="input-base flex items-center gap-2"
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => setOpen(v => !v)}
            >
                {selectedId ? (
                    <span className="flex-1 text-sm truncate" style={{ color: 'var(--text-primary)' }}>
                        {selectedName}
                    </span>
                ) : (
                    <span className="flex-1 text-sm" style={{ color: 'var(--text-tertiary)' }}>
                        {placeholder}
                    </span>
                )}
                {selectedId && (
                    <button
                        onClick={e => { e.stopPropagation(); onChange('', ''); setOpen(false); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: 0, display: 'flex' }}
                        title="Clear"
                    >
                        <span style={{ fontSize: 16, lineHeight: 1 }}>Ã—</span>
                    </button>
                )}
            </div>

            {open && (
                <div
                    className="card"
                    style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
                        zIndex: 30, border: '1px solid var(--border-default)',
                        boxShadow: 'var(--shadow-md, 0 4px 12px rgba(0,0,0,.12))',
                        maxHeight: 280, display: 'flex', flexDirection: 'column',
                    }}
                >
                    {/* Search */}
                    <div className="flex items-center gap-2 px-3 py-2 border-b flex-shrink-0" style={{ borderColor: 'var(--border-default)' }}>
                        <Search size={13} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                        <input
                            autoFocus
                            type="text"
                            className="flex-1 bg-transparent border-none outline-none text-sm"
                            style={{ color: 'var(--text-primary)' }}
                            placeholder="Searchâ€¦"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        {loading && <Loader2 size={13} className="animate-spin" style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />}
                    </div>

                    {/* Results */}
                    <div className="overflow-auto flex-1">
                        {results.length === 0 ? (
                            <p className="text-sm px-3 py-3" style={{ color: 'var(--text-tertiary)' }}>
                                {loading ? 'Loadingâ€¦' : 'No classification types found'}
                            </p>
                        ) : (
                            results.map(ct => (
                                <button
                                    key={ct.id}
                                    className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors"
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-primary)' }}
                                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                    onClick={() => handleSelect(ct.id, ct.name)}
                                >
                                    {ct.id === selectedId && <Check size={13} style={{ color: 'var(--accent)', flexShrink: 0 }} />}
                                    <span className={ct.id === selectedId ? 'font-medium' : ''}>{ct.name}</span>
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

/* ------------------------------------------------------------------ */
/*  Single classification entry card                                   */
/* ------------------------------------------------------------------ */

type EntryCardProps = {
    entry: ClassificationData;
    onChange: (updated: ClassificationData) => void;
    onRemove: () => void;
};

const EntryCard = ({ entry, onChange, onRemove }: EntryCardProps) => {
    const set = <K extends keyof ClassificationData>(key: K, value: ClassificationData[K]) =>
        onChange({ ...entry, [key]: value });

    return (
        <div className="card" style={{ border: '1px solid var(--border-default)', overflow: 'hidden' }}>
            {/* Header */}
            <div
                className="px-4 py-2.5 border-b flex items-center gap-3"
                style={{
                    borderColor: 'var(--border-default)',
                    backgroundColor: 'var(--surface-1, var(--surface-100))',
                }}
            >
                <span className="text-sm font-semibold flex-1" style={{ color: 'var(--text-primary)' }}>
                    {entry.ClassificationTypeName}
                </span>
                <span className="text-xs font-mono" style={{ color: 'var(--text-tertiary)' }}>
                    {entry.ClassificationTypeId}
                </span>
                <button
                    onClick={onRemove}
                    title="Remove classification"
                    style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: 'var(--text-tertiary)', padding: 2, display: 'flex', borderRadius: 4,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.color = 'var(--danger)'; }}
                    onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-tertiary)'; }}
                >
                    <Trash2 size={14} />
                </button>
            </div>

            {/* Parameter mapping */}
            <div className="p-4 space-y-4 border-b" style={{ borderColor: 'var(--border-subtle, var(--border-default))' }}>
                <p className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Revit Parameter Mapping</p>
                <div>
                    <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>
                        Code Parameter Name
                    </label>
                    <input
                        type="text"
                        className="input-base w-full"
                        placeholder="e.g. Code"
                        value={entry.CodeParameterName ?? ''}
                        onChange={e => set('CodeParameterName', e.target.value || null)}
                    />
                </div>
                <div>
                    <label className="text-xs font-medium mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>
                        Description Parameter Name
                    </label>
                    <input
                        type="text"
                        className="input-base w-full"
                        placeholder="e.g. ClassificationDescription"
                        value={entry.DescriptionParameterName ?? ''}
                        onChange={e => set('DescriptionParameterName', e.target.value || null)}
                    />
                </div>
            </div>

            {/* Options */}
            <div className="px-4 divide-y" style={{ borderColor: 'var(--border-subtle, var(--border-default))' }}>
                <Toggle
                    checked={entry.MapToOotbIfcParams}
                    onChange={v => set('MapToOotbIfcParams', v)}
                    label="Map to OOTB IFC Parameters"
                    description="Write classification data to the built-in Revit IFC parameters"
                />
                <Toggle
                    checked={entry.MapToIfcClassification}
                    onChange={v => set('MapToIfcClassification', v)}
                    label="Map to IFC Classification"
                    description="Assign the IFC classification reference on export"
                />
                <Toggle
                    checked={entry.UpdateCategoryBindings}
                    onChange={v => set('UpdateCategoryBindings', v)}
                    label="Update Category Bindings"
                    description="Rebind the shared parameter to the correct Revit category if it changes"
                />
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

type ClassificationStandardProps = {
    projectId: string;
    style?: CSSProperties;
    onBack: () => void;
};

const ClassificationStandard = ({ projectId, style, onBack }: ClassificationStandardProps) => {
    const [entries, setEntries] = useState<ClassificationMap>({});
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingBlob, setIsLoadingBlob] = useState(false);
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');

    const download = useDownloadAttachment();
    const httpClient = useLoadHttpClientEmpty();

    /* â”€â”€ Query â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
    const { data: queryData, loading: queryLoading, refetch } = useQuery(
        GET_PROJECT_CLASSIFICATION_DATA,
        { variables: { id: projectId }, skip: !projectId, fetchPolicy: 'network-only' },
    );

    const [setDynamicDataMutation] = useMutation(SET_PROJECT_DYNAMIC_DATA);

    const existingEntry = useMemo(() => {
        const dynamicEntries: any[] = queryData?.Project?.dynamicData ?? [];
        return dynamicEntries.find(
            d => d?.type === DYNAMIC_DATA_TYPE && d?.name === DYNAMIC_DATA_NAME,
        ) as { id: string; name: string; type: string; data?: string; setData?: string } | undefined;
    }, [queryData]);

    /* â”€â”€ Load blob â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
    useEffect(() => {
        if (queryLoading || !existingEntry?.data) return;
        setIsLoadingBlob(true);
        download(existingEntry.data)
            .then(raw => {
                const str = typeof raw === 'string' ? raw : JSON.stringify(raw);
                const parsed = JSON.parse(str);
                // Support both legacy single-entry format and current map format
                if (parsed && typeof parsed === 'object' && !parsed.ClassificationTypeId) {
                    setEntries(parsed as ClassificationMap);
                } else if (parsed?.ClassificationTypeId) {
                    setEntries({ [parsed.ClassificationTypeId]: parsed as ClassificationData });
                }
            })
            .catch(() => {})
            .finally(() => setIsLoadingBlob(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingEntry?.data, queryLoading]);

    /* â”€â”€ Entry helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
    const addedIds = useMemo(() => Object.keys(entries), [entries]);

    const handleAddEntry = useCallback((id: string, name: string) => {
        if (!id || entries[id]) return;
        setEntries(prev => ({ ...prev, [id]: makeEntry(id, name) }));
        setIsDirty(true);
    }, [entries]);

    const handleUpdateEntry = useCallback((id: string, updated: ClassificationData) => {
        setEntries(prev => ({ ...prev, [id]: updated }));
        setIsDirty(true);
    }, []);

    const handleRemoveEntry = useCallback((id: string) => {
        setEntries(prev => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
        setIsDirty(true);
    }, []);

    /* â”€â”€ Save â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
    const handleSave = useCallback(async () => {
        setIsSaving(true);
        setSaveStatus('idle');
        try {
            if (!existingEntry) {
                await setDynamicDataMutation({
                    variables: {
                        projectId,
                        data: {
                            name: DYNAMIC_DATA_NAME,
                            type: DYNAMIC_DATA_TYPE,
                            description: 'Classification standard settings',
                        },
                    },
                });
            }
            const refetched = await refetch();
            const entry: any = (refetched.data?.Project?.dynamicData ?? []).find(
                (d: any) => d?.type === DYNAMIC_DATA_TYPE && d?.name === DYNAMIC_DATA_NAME,
            );
            if (!entry?.setData) throw new Error('No upload URL returned');
            await httpClient.put(entry.setData, JSON.stringify(entries), {
                headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': 'application/json', 'Cache-Control': 'no-cache, no-store, must-revalidate' },
            });
            setIsDirty(false);
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus('idle'), 3000);
        } catch {
            setSaveStatus('error');
            setTimeout(() => setSaveStatus('idle'), 4000);
        } finally {
            setIsSaving(false);
        }
    }, [entries, existingEntry, projectId, setDynamicDataMutation, httpClient, refetch]);

    /* â”€â”€ Navigation guard â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
    const handleBack = useCallback(() => {
        if (isDirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
        onBack();
    }, [isDirty, onBack]);

    const isLoading = queryLoading || isLoadingBlob;
    const entryList = useMemo(() => Object.values(entries), [entries]);

    /* â”€â”€ Render â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
    return (
        <div className="flex flex-col h-full" style={style} data-testid="ClassificationStandard">

            {/* Toolbar */}
            <div
                className="flex items-center gap-3 px-4 py-2.5 border-b flex-shrink-0"
                style={{ borderColor: 'var(--border-default)' }}
            >
                <button
                    className="flex items-center gap-1 text-sm font-medium px-2 py-1 rounded-md"
                    style={{ color: 'var(--accent-text)', background: 'none', border: 'none', cursor: 'pointer' }}
                    onClick={handleBack}
                >
                    <ArrowLeft size={15} /> Back
                </button>
                <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                    Classification
                </span>
                <div className="flex-1" />
                {saveStatus === 'saved' && (
                    <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--success, #16a34a)' }}>
                        <Check size={13} /> Saved
                    </span>
                )}
                {saveStatus === 'error' && (
                    <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--danger)' }}>
                        <AlertCircle size={13} /> Save failed
                    </span>
                )}
                {isDirty && saveStatus === 'idle' && (
                    <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Unsaved changes</span>
                )}
                <button
                    className="btn-primary flex items-center gap-2 text-sm"
                    disabled={!isDirty || isSaving}
                    onClick={handleSave}
                    style={{ opacity: !isDirty || isSaving ? 0.5 : 1 }}
                >
                    {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Save
                </button>
            </div>

            {/* Content */}
            {isLoading ? (
                <div className="flex items-center justify-center flex-1">
                    <Loader2 size={20} className="animate-spin" style={{ color: 'var(--text-tertiary)' }} />
                </div>
            ) : (
                <div className="flex-1 overflow-auto px-6 py-5" style={{ paddingBottom: '18rem' }}>
                    <div style={{ maxWidth: 620 }} className="space-y-4">

                        {/* Empty state */}
                        {entryList.length === 0 && (
                            <div
                                className="rounded-lg text-center py-10"
                                style={{ border: '2px dashed var(--border-default)', color: 'var(--text-tertiary)' }}
                            >
                                <p className="text-sm">No classifications configured.</p>
                                <p className="text-xs mt-1">Use the picker below to add one.</p>
                            </div>
                        )}

                        {/* Entry cards â€” one per classification */}
                        {entryList.map(entry => (
                            <EntryCard
                                key={entry.ClassificationTypeId}
                                entry={entry}
                                onChange={updated => handleUpdateEntry(entry.ClassificationTypeId, updated)}
                                onRemove={() => handleRemoveEntry(entry.ClassificationTypeId)}
                            />
                        ))}

                        {/* Add classification picker */}
                        <div
                            className="card"
                            style={{ border: '1px dashed var(--border-default)' }}
                        >
                            <div
                                className="px-4 py-2.5 border-b flex items-center gap-2"
                                style={{
                                    borderColor: 'var(--border-default)',
                                    backgroundColor: 'var(--surface-1, var(--surface-100))',
                                }}
                            >
                                <Plus size={14} style={{ color: 'var(--text-tertiary)' }} />
                                <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                                    Add Classification
                                </span>
                            </div>
                            <div className="p-4">
                                <ClassificationTypePicker
                                    selectedId=""
                                    selectedName=""
                                    excludeIds={addedIds}
                                    placeholder="Search and select a classification type to addâ€¦"
                                    onChange={handleAddEntry}
                                />
                            </div>
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
};

export default ClassificationStandard;
